import React, { useState, useEffect } from "react";
import { Form, Grid, Header, Label, Icon, Dropdown } from "semantic-ui-react";
import Joi from "joi";
import { isEmpty, map, isEqual, chunk, get, trim, reduce, filter, isArray, includes, flatMap } from "lodash";
import PropTypes from "prop-types";

// redux
import { connect } from "react-redux";
import genericAction from "@Actions/generic";

// Components
import MenuSelect from "@Components/MenuSelect";
import Input from "@Components/Input";
import IKModal from "@Components/IkModal";

// Modals
import ConfirmModal from "@Modals/ConfirmModal";

// Styles
import styles from "./styles.module.css";

// utils
import translateActionChat, { orderedPermissions } from "./staticData";

const Profile = ({ modalOption, tableValue, menuData, onClose, userPermissions, companySettings, apiData, dispatch }) => {
	const schema = Joi.object().keys({
		name: Joi.string().min(3).max(50).messages({
			"string.min": "Nombre debe tener mínimo 3 caracteres",
			"string.empty": "Nombre es un campo requerido",
			"string.max": "Nombre debe tener máximo 50 caracteres",
		}),

		menu: Joi.array().min(1).messages({
			"array.min": "Menú es un campo requerido",
		}),

		defaultMenu: Joi.number().required().messages({
			"any.required": "Debe seleccionar un menú por defecto",
			"number.base": "Debe seleccionar un menú por defecto",
		}),
	});
	const [open, setOpen] = useState(false);
	const [errors, setErrors] = useState({ name: "", code: "", defaultMenu: "" });
	const [name, setName] = useState("");
	const [permissions, setPermissions] = useState([]);

	const [boolCrud, setBoolCrud] = useState("");
	const [menu, setMenu] = useState([]);
	const [defaultMenu, setDefaultMenu] = useState(null);
	const [initialFormData, setInitialFormData] = useState({ name: "", menu: [], permissions: [], defaultMenu: null });

	useEffect(() => {
		const sendFlowEnabled = get(companySettings, "sendFlow", false);

		if (!isEmpty(tableValue)) {
			setName(get(tableValue, "name", ""));
			const defaultPermissions = reduce(
				userPermissions,
				(acc, _value, key) => {
					acc[key] = false;
					return acc;
				},
				{}
			);

			if (sendFlowEnabled && !("allowSendFlow" in defaultPermissions)) {
				defaultPermissions.allowSendFlow = false;
			}
			if (sendFlowEnabled && !("allowConfigFlow" in defaultPermissions)) {
				defaultPermissions.allowConfigFlow = false;
			}

			const mergedPermissions = {
				...defaultPermissions,
				...get(tableValue, "permissions", {}),
			};

			const menuFromTable = get(tableValue, "menu", []);
			const defaultMenuFromTable = get(tableValue, "defaultMenuId", null);

			// Validar si el defaultMenu está en el menu, si no, tomar el primero
			let finalDefaultMenu = defaultMenuFromTable;
			if (isArray(menuFromTable) && menuFromTable.length > 0) {
				if (!defaultMenuFromTable || !includes(menuFromTable, defaultMenuFromTable)) {
					[finalDefaultMenu] = menuFromTable;
				}
			} else {
				finalDefaultMenu = null;
			}

			setPermissions(mergedPermissions);
			setMenu(menuFromTable);
			setDefaultMenu(finalDefaultMenu);
			setInitialFormData({
				name: get(tableValue, "name", ""),
				menu: menuFromTable,
				permissions: mergedPermissions,
				defaultMenu: finalDefaultMenu,
			});
		} else {
			const defaultTrueKeys = ["allowTransferChat", "allowTakeChat", "allowFreeChat", "allowEndChat"];

			const permissionsByDefault = reduce(
				userPermissions,
				(acc, _value, key) => {
					if (key !== "allowChatAssign") {
						acc[key] = defaultTrueKeys.includes(key);
					}
					return acc;
				},
				{}
			);

			if (sendFlowEnabled && !("allowSendFlow" in permissionsByDefault)) {
				permissionsByDefault.allowSendFlow = false;
			}
			if (sendFlowEnabled && !("allowConfigFlow" in permissionsByDefault)) {
				permissionsByDefault.allowConfigFlow = false;
			}

			setPermissions(permissionsByDefault);
			setInitialFormData({
				name: "",
				menu: [],
				permissions: permissionsByDefault,
				defaultMenu: null,
			});
		}
		setBoolCrud(modalOption);
	}, [tableValue]);

	// Crear opciones para el dropdown de defaultMenu
	const menuOptions = flatMap(
		filter(menuData || [], (item) => {
			return isArray(menu) && menu.includes(item.id);
		}),
		(item) => {
			const parentOption = {
				key: item.id,
				text: item.name,
				value: item.id,
			};

			// subMenus seleccionados en `menu`
			const subOptions = (item.subMenu || [])
				.filter((sub) => {
					return isArray(menu) && menu.includes(sub.id);
				})
				.map((sub) => {
					return {
						key: sub.id,
						text: `${item.name} › ${sub.name}`,
						value: sub.id,
					};
				});

			return [parentOption, ...subOptions];
		}
	);

	// save handlers
	const handlerSaved = async () => {
		const validation = schema.validate(
			{
				menu,
				name,
				defaultMenu,
			},

			{ abortEarly: false }
		);

		if (validation.error) {
			const { details } = validation.error;
			let errorsData = {
				name: "",
				code: "",
				defaultMenu: "",
			};
			map(details, (item) => {
				errorsData = { ...errorsData, [item.context.key]: item.message };
			});
			setErrors(errorsData);
		} else {
			const data = { name: trim(name), permissions, menu, defaultMenuId: defaultMenu == null ? null : Number(defaultMenu) };
			switch (boolCrud) {
				case "Add": {
					const dataAdd = await dispatch(genericAction({ method: "post", route: `profiles`, body: data }));

					if (!isEmpty(dataAdd)) {
						const permissionsInFalse = Object.keys(userPermissions).reduce((acc, key) => {
							acc[key] = false;
							return acc;
						}, {});

						setName("");
						setMenu([]);
						setDefaultMenu(null);
						setPermissions(permissionsInFalse);
						setInitialFormData({ name: "", menu: [], permissions: permissionsInFalse, defaultMenu: null });
						setErrors({ name: "", code: "", defaultMenu: "" });
						apiData("Add");
					}
					break;
				}

				case "Edit": {
					const oldMenu = get(initialFormData, "menu", []);
					const newMenuItems = (menu || []).filter((id) => {
						return !oldMenu.includes(id);
					});

					const dataEdit = await dispatch(genericAction({ method: "patch", route: `profiles/${tableValue.id}`, body: data }));
					if (!isEmpty(dataEdit)) {
						if (newMenuItems.length > 0) {
							const usersResponse = await dispatch(genericAction({ method: "get", route: "users?page=1&rows=10000&isActive=1" }));
							const usersList = get(usersResponse, "items", []);
							const profileName = trim(name);

							for (const userData of usersList) {
								if (userData.role === profileName) {
									const userMenu = get(userData, "menu", []);
									const mergedMenu = [...new Set([...userMenu, ...newMenuItems])];
									if (mergedMenu.length > userMenu.length) {
										await dispatch(
											genericAction({
												method: "patch",
												route: `users/${userData.userId}`,
												body: { menu: mergedMenu },
											})
										);
									}
								}
							}
						}

						setName("");
						setMenu([]);
						setDefaultMenu(null);
						setPermissions(userPermissions);
						setInitialFormData({ name: "", menu: [], permissions: userPermissions, defaultMenu: null });
						setErrors({ name: "", code: "", defaultMenu: "" });
						apiData("Edit");
					}
					break;
				}
				default: {
					break;
				}
			}
		}
	};

	// modal handlers
	const modalOpen = () => {
		if (isEqual(initialFormData, { name, menu, permissions, defaultMenu })) {
			onClose();
		} else if (open) {
			setOpen(false);
		} else {
			setOpen(true);
		}
	};
	const onChangePermissions = (e, data) => {
		setPermissions({
			...permissions,
			[data.name]: data.checked,
		});
	};

	return (
		<>
			<IKModal
				title={modalOption === "Add" ? "Agregar Perfil" : `Perfil: ${get(tableValue, "name", "")} `}
				id={tableValue.id}
				mode={boolCrud}
				setMode={setBoolCrud}
				onClose={modalOpen}
				onSuccess={handlerSaved}
				size="small">
				<Grid columns={2} className={styles.grid}>
					<Grid.Row>
						<Grid.Column width="10" className={styles.column}>
							<Form.Field className={styles.field}>
								<div className={styles.headerInput}>
									<span className={styles.nameClass}>
										Nombre: <span className={styles.spanClass}>*</span>
									</span>
									<span className={`${styles.counter} ${name.length > 50 ? styles.counterError : ""}`}>{name.length} / 50</span>
								</div>

								<Input
									icon="list"
									placeholder="Nombre del perfil"
									disabled={boolCrud === "Watch"}
									name="hotkey"
									fluid
									required
									className={styles.input}
									value={name}
									onChange={(e) => {
										return setName(e.target.value);
									}}
									error={!!errors.name}
								/>
								{!isEmpty(errors.name) && (
									<Label basic color="red" pointing="above" className={styles.labelError}>
										{errors.name}
									</Label>
								)}
							</Form.Field>

							<Form.Field className={styles.field}>
								<div className={styles.headerInput}>
									<span className={styles.nameClass}>
										Default Menú: <span className={styles.spanClass}>*</span>
									</span>
								</div>
								<Dropdown
									placeholder="Seleccione una opción"
									fluid
									selection
									name="defaultMenu"
									value={defaultMenu}
									onChange={(e, { value }) => {
										setDefaultMenu(value == null ? null : Number(value));
										setErrors((prev) => {
											return { ...prev, defaultMenu: "" };
										});
									}}
									options={menuOptions}
									disabled={boolCrud === "Watch" || !menu || menu.length === 0}
									className={styles.dropdown}
								/>
								{!isEmpty(errors.defaultMenu) && (
									<Label basic color="red" pointing="above" className={styles.labelError}>
										{errors.defaultMenu}
									</Label>
								)}
							</Form.Field>

							<fieldset className={styles.fieldsetPermissions}>
								<legend className={styles.legend}>
									<Header className={styles.titleBox} as="h3" icon="key" content="Permisos" />
								</legend>
								<Form>
									{map(
										chunk(
											orderedPermissions.filter((key) => {
												if (key === "allowSendFlow" || key === "allowConfigFlow") {
													return get(companySettings, "sendFlow", false);
												}
												return key in permissions;
											}),
											2
										),
										(e, i) => {
											const [l, r] = e;
											return (
												<Form.Group key={i}>
													<Form.Checkbox
														toggle
														label={translateActionChat[l] || l}
														disabled={boolCrud === "Watch"}
														name={l}
														checked={permissions[l]}
														onChange={onChangePermissions}
														width="8"
													/>
													{r && (
														<Form.Checkbox
															toggle
															label={translateActionChat[r] || r}
															disabled={boolCrud === "Watch"}
															name={r}
															checked={permissions[r]}
															onChange={onChangePermissions}
															width="8"
														/>
													)}
												</Form.Group>
											);
										}
									)}
								</Form>
							</fieldset>
						</Grid.Column>

						<Grid.Column width="6" className={styles.column2}>
							<fieldset className={styles.fieldsetMenu}>
								<legend className={styles.legend}>
									<Header className={styles.titleBoxB} as="h3" icon="sidebar">
										<Icon className={styles.icons} name="sidebar" />
										Menú <span className={styles.required}>*</span>
									</Header>
								</legend>
								{errors.menu && (
									<div>
										<Label basic color="red" pointing="above">
											{errors.menu}
										</Label>
									</div>
								)}
								<MenuSelect
									inValue={menu}
									menuData={menuData}
									disabled={boolCrud === "Watch"}
									onValue={(value) => {
										return setMenu(value);
									}}
								/>
							</fieldset>
						</Grid.Column>
					</Grid.Row>
				</Grid>
			</IKModal>

			{open && (
				<ConfirmModal
					buttonLabel="Salir"
					visible
					header="Salir del formulario"
					content="Está seguro que desea salir del formulario"
					onConfirm={onClose}
					onDismiss={modalOpen}
				/>
			)}
		</>
	);
};

function mapStateToProps({ user, company }) {
	return {
		companyName: company.name,
		userName: user.name,
		userPermissions: user.permissions,
		companySettings: company.settings,
	};
}
Profile.propTypes = {
	modalOption: PropTypes.string,
	tableValue: PropTypes.object,
	menuData: PropTypes.array,
	onClose: PropTypes.func,
	userPermissions: PropTypes.object,
	companySettings: PropTypes.object,
	apiData: PropTypes.func,
	dispatch: PropTypes.func,
};
export default connect(mapStateToProps)(Profile);
